import React from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';

// ── Shared color constants ───────────────────────────────────────────────────
export const DOCUMENT_COLORS = {
  brand: '#0d9488',
  brandDark: '#0f766e',
  brandLight: '#f0fdfa',
  sectionBg: '#FAFBFC',
  sectionBgHover: '#F8FAFC',
  dialogBg: '#F1F5F9',
  fieldLabel: '#64748B',
  titleText: '#1E293B',
  border: '#E2E8F0',
  cardBg: '#fff',
};

// ── AppBar gradient ──────────────────────────────────────────────────────────
export const APPBAR_GRADIENT = 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)';

// ── Markdown-to-JSX renderer ─────────────────────────────────────────────────
export const renderMarkdown = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let listItems = [];
  let listKey = 0;

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <Box component="ul" key={`list-${listKey++}`} sx={{ pl: 2.5, my: 1, '& li': { mb: 0.5 } }}>
          {listItems}
        </Box>
      );
      listItems = [];
    }
  };

  const formatInline = (line) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  lines.forEach((line, idx) => {
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    // Bullet point: * item, - item, or bullet char
    const bulletMatch = trimmed.match(/^[*\-\u2022]\s+(.+)/);
    if (bulletMatch) {
      listItems.push(
        <Typography component="li" variant="body2" key={`li-${idx}`} sx={{ lineHeight: 1.7 }}>
          {formatInline(bulletMatch[1])}
        </Typography>
      );
      return;
    }

    // Numbered list: 1. item
    const numberedMatch = trimmed.match(/^\d+\.\s+(.+)/);
    if (numberedMatch) {
      listItems.push(
        <Typography component="li" variant="body2" key={`li-${idx}`} sx={{ lineHeight: 1.7 }}>
          {formatInline(numberedMatch[1])}
        </Typography>
      );
      return;
    }

    flushList();

    elements.push(
      <Typography variant="body2" key={`p-${idx}`} sx={{ lineHeight: 1.7, mb: 0.5 }}>
        {formatInline(trimmed)}
      </Typography>
    );
  });

  flushList();
  return elements;
};

// ── Markdown stripper for one/two-line previews ──────────────────────────────
// Line-clamped snippets can't show lists/bold, so drop the syntax instead of
// showing literal ** and * characters.
export const stripMarkdown = (text) => {
  if (!text) return '';
  return String(text)
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[*\-•]\s+/gm, '')
    .replace(/\s+/g, ' ')
    .trim();
};

// ── SectionWrapper ───────────────────────────────────────────────────────────
// Blue left-border accent wrapper for each section
export const SectionWrapper = ({ children, sx = {} }) => (
  <Box
    sx={{
      mb: 4,
      p: 3,
      borderLeft: `4px solid ${DOCUMENT_COLORS.brand}`,
      borderRadius: '0 8px 8px 0',
      bgcolor: DOCUMENT_COLORS.sectionBg,
      '&:hover': { bgcolor: DOCUMENT_COLORS.sectionBgHover },
      transition: 'background-color 0.2s',
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ── SectionHeader ────────────────────────────────────────────────────────────
// Icon badge + title + optional edit/save/cancel buttons
export const SectionHeader = ({
  icon,
  title,
  editing = false,
  onEdit,
  onSave,
  onCancel,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2.5,
      pb: 1.5,
      borderBottom: `2px solid ${DOCUMENT_COLORS.border}`,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      {icon && (
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: DOCUMENT_COLORS.brandLight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: DOCUMENT_COLORS.brand,
          }}
        >
          {icon}
        </Box>
      )}
      <Typography variant="h6" sx={{ fontWeight: 700, color: DOCUMENT_COLORS.titleText }}>
        {title}
      </Typography>
    </Box>
    {onEdit && (
      <Box className="no-print">
        {!editing && (
          <IconButton size="small" onClick={onEdit} sx={{ color: '#94A3B8' }}>
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {editing && (
          <Box>
            <IconButton size="small" color="primary" onClick={onSave}>
              <SaveIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onCancel}>
              <CancelIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
    )}
  </Box>
);

// ── FieldLabel ───────────────────────────────────────────────────────────────
// Uppercase caption-styled label for document fields
export const FieldLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{
      color: DOCUMENT_COLORS.fieldLabel,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      display: 'block',
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

// ── CardField ────────────────────────────────────────────────────────────────
// White card box for short inline fields (like Work Type, Location)
export const CardField = ({ children }) => (
  <Box
    sx={{
      p: 2,
      bgcolor: DOCUMENT_COLORS.cardBg,
      borderRadius: 1,
      border: `1px solid ${DOCUMENT_COLORS.border}`,
    }}
  >
    {children}
  </Box>
);
